/**
 * Vietnamese / Chinese lunar (âm lịch / 农历) calendar conversion.
 *
 * Faithful port of Hồ Ngọc Đức's public-domain astronomical algorithm
 * (valid for solar years ~1800–2199).
 *
 * Vietnam and China compute the same new-moon / solar-term astronomy but at
 * different reference longitudes (Hanoi UTC+7 vs Beijing UTC+8), so the two
 * calendars occasionally differ by a day around a new-moon boundary — pass
 * the matching `timeZone` (7 or 8) for each.
 *
 * Shared by temple specials (event date conversion) and the web SPA.
 */

const PI = Math.PI;

/** Largest integer not exceeding `d` (i.e. `Math.floor`, kept for parity with the reference algorithm). */
function INT(d: number): number {
  return Math.floor(d);
}

function jdFromDate(dd: number, mm: number, yy: number): number {
  const a = INT((14 - mm) / 12);
  const y = yy + 4800 - a;
  const m = mm + 12 * a - 3;
  let jd =
    dd +
    INT((153 * m + 2) / 5) +
    365 * y +
    INT(y / 4) -
    INT(y / 100) +
    INT(y / 400) -
    32045;
  if (jd < 2299161) {
    jd = dd + INT((153 * m + 2) / 5) + 365 * y + INT(y / 4) - 32083;
  }
  return jd;
}

function jdToDate(jd: number): { day: number; month: number; year: number } {
  let a: number;
  let b: number;
  let c: number;
  if (jd > 2299160) {
    // After 5/10/1582, Gregorian calendar
    a = jd + 32044;
    b = INT((4 * a + 3) / 146097);
    c = a - INT((b * 146097) / 4);
  } else {
    b = 0;
    c = jd + 32082;
  }
  const d = INT((4 * c + 3) / 1461);
  const e = c - INT((1461 * d) / 4);
  const m = INT((5 * e + 2) / 153);
  const day = e - INT((153 * m + 2) / 5) + 1;
  const month = m + 3 - 12 * INT(m / 10);
  const year = b * 100 + d - 4800 + INT(m / 10);
  return { day, month, year };
}

function getNewMoonDay(k: number, timeZone: number): number {
  const T = k / 1236.85; // Time in Julian centuries from 1900 January 0.5
  const T2 = T * T;
  const T3 = T2 * T;
  const dr = PI / 180;
  let Jd1 = 2415020.75933 + 29.53058868 * k + 0.0001178 * T2 - 0.000000155 * T3;
  Jd1 += 0.00033 * Math.sin((166.56 + 132.87 * T - 0.009173 * T2) * dr); // Mean new moon
  const M = 359.2242 + 29.10535608 * k - 0.0000333 * T2 - 0.00000347 * T3; // Sun's mean anomaly
  const Mpr = 306.0253 + 385.81691806 * k + 0.0107306 * T2 + 0.00001236 * T3; // Moon's mean anomaly
  const F = 21.2964 + 390.67050646 * k - 0.0016528 * T2 - 0.00000239 * T3; // Moon's argument of latitude
  let C1 = (0.1734 - 0.000393 * T) * Math.sin(M * dr) + 0.0021 * Math.sin(2 * dr * M);
  C1 = C1 - 0.4068 * Math.sin(Mpr * dr) + 0.0161 * Math.sin(dr * 2 * Mpr);
  C1 = C1 - 0.0004 * Math.sin(dr * 3 * Mpr);
  C1 = C1 + 0.0104 * Math.sin(dr * 2 * F) - 0.0051 * Math.sin(dr * (M + Mpr));
  C1 = C1 - 0.0074 * Math.sin(dr * (M - Mpr)) + 0.0004 * Math.sin(dr * (2 * F + M));
  C1 = C1 - 0.0004 * Math.sin(dr * (2 * F - M)) - 0.0006 * Math.sin(dr * (2 * F + Mpr));
  C1 = C1 + 0.001 * Math.sin(dr * (2 * F - Mpr)) + 0.0005 * Math.sin(dr * (2 * Mpr + M));
  let deltat: number;
  if (T < -11) {
    deltat = 0.001 + 0.000839 * T + 0.0002261 * T2 - 0.00000845 * T3 - 0.000000081 * T * T3;
  } else {
    deltat = -0.000278 + 0.000265 * T + 0.000262 * T2;
  }
  const JdNew = Jd1 + C1 - deltat;
  return INT(JdNew + 0.5 + timeZone / 24);
}

function getSunLongitude(jdn: number, timeZone: number): number {
  const T = (jdn - 2451545.5 - timeZone / 24) / 36525; // Julian centuries from 2000-01-01 12:00 GMT
  const T2 = T * T;
  const dr = PI / 180;
  const M = 357.5291 + 35999.0503 * T - 0.0001559 * T2 - 0.00000048 * T * T2; // mean anomaly
  const L0 = 280.46645 + 36000.76983 * T + 0.0003032 * T2; // mean longitude
  let DL = (1.9146 - 0.004817 * T - 0.000014 * T2) * Math.sin(dr * M);
  DL = DL + (0.019993 - 0.000101 * T) * Math.sin(dr * 2 * M) + 0.00029 * Math.sin(dr * 3 * M);
  let L = L0 + DL; // true longitude, degree
  L = L * dr;
  L = L - PI * 2 * INT(L / (PI * 2)); // normalize to (0, 2*PI)
  return INT((L / PI) * 6);
}

function getLunarMonth11(yy: number, timeZone: number): number {
  const off = jdFromDate(31, 12, yy) - 2415021;
  const k = INT(off / 29.530588853);
  let nm = getNewMoonDay(k, timeZone);
  const sunLong = getSunLongitude(nm, timeZone); // sun longitude at local midnight
  if (sunLong >= 9) {
    nm = getNewMoonDay(k - 1, timeZone);
  }
  return nm;
}

function getLeapMonthOffset(a11: number, timeZone: number): number {
  const k = INT((a11 - 2415021.076998695) / 29.530588853 + 0.5);
  let last = 0;
  let i = 1; // Start with the month following lunar month 11
  let arc = getSunLongitude(getNewMoonDay(k + i, timeZone), timeZone);
  do {
    last = arc;
    i++;
    arc = getSunLongitude(getNewMoonDay(k + i, timeZone), timeZone);
  } while (arc !== last && i < 14);
  return i - 1;
}

export interface LunarDate {
  day: number;
  month: number;
  year: number;
  /** True when `month` is a leap month (tháng nhuận / 闰月). */
  leap: boolean;
}

export interface SolarDate {
  day: number;
  month: number;
  year: number;
}

/**
 * Convert a solar (Gregorian) date to its lunar equivalent.
 * `timeZone`: 7 for the Vietnamese calendar (Hanoi, UTC+7), 8 for the
 * Chinese calendar (Beijing, UTC+8).
 */
export function solarToLunar(
  dd: number,
  mm: number,
  yy: number,
  timeZone: number,
): LunarDate {
  const dayNumber = jdFromDate(dd, mm, yy);
  const k = INT((dayNumber - 2415021.076998695) / 29.530588853);
  let monthStart = getNewMoonDay(k + 1, timeZone);
  if (monthStart > dayNumber) {
    monthStart = getNewMoonDay(k, timeZone);
  }
  let a11 = getLunarMonth11(yy, timeZone);
  let b11 = a11;
  let lunarYear: number;
  if (a11 >= monthStart) {
    lunarYear = yy;
    a11 = getLunarMonth11(yy - 1, timeZone);
  } else {
    lunarYear = yy + 1;
    b11 = getLunarMonth11(yy + 1, timeZone);
  }
  const lunarDay = dayNumber - monthStart + 1;
  const diff = INT((monthStart - a11) / 29);
  let lunarLeap = false;
  let lunarMonth = diff + 11;
  if (b11 - a11 > 365) {
    const leapMonthDiff = getLeapMonthOffset(a11, timeZone);
    if (diff >= leapMonthDiff) {
      lunarMonth = diff + 10;
      if (diff === leapMonthDiff) {
        lunarLeap = true;
      }
    }
  }
  if (lunarMonth > 12) {
    lunarMonth -= 12;
  }
  if (lunarMonth >= 11 && diff < 4) {
    lunarYear -= 1;
  }
  return { day: lunarDay, month: lunarMonth, year: lunarYear, leap: lunarLeap };
}

/**
 * Convert a lunar date to its solar (Gregorian) equivalent.
 * `lunarLeap`: true when the given month is a leap month.
 * Returns null when the combination is invalid (e.g. leap requested for a
 * non-leap month in that year).
 * `timeZone`: 7 for Vietnamese calendar (default for WLotus temple specials).
 */
export function lunarToSolar(
  lunarDay: number,
  lunarMonth: number,
  lunarYear: number,
  lunarLeap: boolean,
  timeZone: number,
): SolarDate | null {
  let a11: number;
  let b11: number;
  if (lunarMonth < 11) {
    a11 = getLunarMonth11(lunarYear - 1, timeZone);
    b11 = getLunarMonth11(lunarYear, timeZone);
  } else {
    a11 = getLunarMonth11(lunarYear, timeZone);
    b11 = getLunarMonth11(lunarYear + 1, timeZone);
  }
  const k = INT(0.5 + (a11 - 2415021.076998695) / 29.530588853);
  let off = lunarMonth - 11;
  if (off < 0) {
    off += 12;
  }
  if (b11 - a11 > 365) {
    const leapOff = getLeapMonthOffset(a11, timeZone);
    let leapMonth = leapOff - 2;
    if (leapMonth < 0) {
      leapMonth += 12;
    }
    if (lunarLeap && lunarMonth !== leapMonth) {
      return null; // invalid leap request
    } else if (lunarLeap || off >= leapOff) {
      off += 1;
    }
  } else if (lunarLeap) {
    return null; // no leap month this year
  }
  const monthStart = getNewMoonDay(k + off, timeZone);
  return jdToDate(monthStart + lunarDay - 1);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatSolarYmd(d: SolarDate): string {
  return `${d.year}-${pad2(d.month)}-${pad2(d.day)}`;
}

function addSolarDaysYmd(ymd: string, deltaDays: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const utc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + deltaDays);
  const dt = new Date(utc);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

/** Convert lunar YYYY-MM-DD (non-leap) to solar YYYY-MM-DD. timeZone default 7 (VN). */
export function lunarYmdToSolarYmd(
  lunarYmd: string,
  timeZone = 7,
  leap = false,
): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(lunarYmd.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 30) return null;
  const solar = lunarToSolar(d, mo, y, leap, timeZone);
  if (!solar) return null;
  return formatSolarYmd(solar);
}

/** Convert solar YYYY-MM-DD to lunar YYYY-MM-DD (leap month is not encoded). */
export function solarYmdToLunarYmd(
  solarYmd: string,
  timeZone = 7,
): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(solarYmd.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const lunar = solarToLunar(d, mo, y, timeZone);
  return `${lunar.year}-${pad2(lunar.month)}-${pad2(lunar.day)}`;
}

/**
 * Solar YYYY-MM-DD of the last day of a non-leap lunar month
 * (Giao thừa / 除夕: last day of tháng Chạp, 29 or 30).
 */
export function lunarMonthLastSolarYmd(
  lunarYear: number,
  lunarMonth: number,
  timeZone = 7,
): string | null {
  if (lunarMonth < 1 || lunarMonth > 12) return null;
  const nextMonth = lunarMonth === 12 ? 1 : lunarMonth + 1;
  const nextYear = lunarMonth === 12 ? lunarYear + 1 : lunarYear;
  const next = lunarToSolar(1, nextMonth, nextYear, false, timeZone);
  if (!next) return null;
  return addSolarDaysYmd(formatSolarYmd(next), -1);
}

/** True when the next solar day is lunar mùng 1 (this ymd is 29 or 30). */
export function isLunarMonthEndYmd(ymd: string, timeZone = 7): boolean {
  const next = addSolarDaysYmd(ymd, 1);
  if (!next) return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(next);
  if (!m) return false;
  return solarToLunar(Number(m[3]), Number(m[2]), Number(m[1]), timeZone).day === 1;
}

export interface MonthlyLunarSpec {
  /** Lunar day-of-month at the close of the window (1 = sóc, 15 = vọng). */
  peakDay: number;
  /** Include the eve: 14 before rằm, last day of the month before mùng 1. */
  includeEve: boolean;
  /** Lunar months where a named festival already covers this rằm/sóc. */
  skipLunarMonths?: number[];
}

function parseSolarYmd(
  ymd: string,
): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

function monthlyPeakSkipped(
  peakYmd: string,
  spec: MonthlyLunarSpec,
  timeZone: number,
): boolean {
  const skip = spec.skipLunarMonths ?? [];
  if (!skip.length) return false;
  const p = parseSolarYmd(peakYmd);
  if (!p) return false;
  const lunar = solarToLunar(p.d, p.m, p.y, timeZone);
  return !lunar.leap && skip.includes(lunar.month);
}

function isMonthlyWindowStart(
  ymd: string,
  spec: MonthlyLunarSpec,
  timeZone: number,
): boolean {
  const p = parseSolarYmd(ymd);
  if (!p) return false;
  const lunar = solarToLunar(p.d, p.m, p.y, timeZone);
  if (!spec.includeEve) return lunar.day === spec.peakDay;
  if (spec.peakDay === 15) return lunar.day === 14;
  if (spec.peakDay === 1) return isLunarMonthEndYmd(ymd, timeZone);
  return lunar.day === spec.peakDay - 1;
}

/**
 * Next 14–rằm or 30–mùng 1 window whose end is today or later.
 * Skips lunar months that already have a named festival (Vu Lan, Nguyên Tiêu, …).
 */
export function nextMonthlyLunarWindow(
  fromYmd: string,
  spec: MonthlyLunarSpec,
  timeZone = 7,
): { start: string; end: string; peak: string } | null {
  if (spec.peakDay < 1 || spec.peakDay > 30) return null;
  const from = fromYmd.trim();
  let ymd = spec.includeEve
    ? addSolarDaysYmd(from, -1) ?? from
    : from;
  for (let i = 0; i < 120; i++) {
    if (isMonthlyWindowStart(ymd, spec, timeZone)) {
      const peak = spec.includeEve ? addSolarDaysYmd(ymd, 1) : ymd;
      if (
        peak &&
        !monthlyPeakSkipped(peak, spec, timeZone)
      ) {
        if (peak >= from) {
          return { start: ymd, end: peak, peak };
        }
      }
    }
    const next = addSolarDaysYmd(ymd, 1);
    if (!next) return null;
    ymd = next;
  }
  return null;
}

export function solarYmdInMonthlyLunar(
  ymd: string,
  spec: MonthlyLunarSpec,
  timeZone = 7,
): boolean {
  const w = nextMonthlyLunarWindow(ymd, spec, timeZone);
  return Boolean(w && ymd >= w.start && ymd <= w.end);
}

/** True when this solar civil day is that lunar day-of-month (1 or 15, …). */
export function solarYmdIsLunarDay(
  ymd: string,
  lunarDay: number,
  timeZone = 7,
): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m || lunarDay < 1 || lunarDay > 30) return false;
  const lunar = solarToLunar(Number(m[3]), Number(m[2]), Number(m[1]), timeZone);
  return lunar.day === lunarDay;
}

/** Next solar YYYY-MM-DD (today or later) whose lunar day-of-month matches. */
export function nextSolarYmdForLunarDay(
  fromYmd: string,
  lunarDay: number,
  timeZone = 7,
): string | null {
  if (lunarDay < 1 || lunarDay > 30) return null;
  let ymd = fromYmd.trim();
  for (let i = 0; i < 46; i++) {
    if (solarYmdIsLunarDay(ymd, lunarDay, timeZone)) return ymd;
    const next = addSolarDaysYmd(ymd, 1);
    if (!next) return null;
    ymd = next;
  }
  return null;
}
